'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';

function IconNextChat({
    className,
    inverted,
    ...props
}: React.ComponentProps<'svg'> & { inverted?: boolean }) {
    const id = React.useId();

    return (
        <svg
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <defs>
                <linearGradient
                    id={`gradient-${id}-1`}
                    x1="10.6889"
                    y1="10.3556"
                    x2="13.8445"
                    y2="14.2667"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={inverted ? 'white' : 'black'} />
                    <stop
                        offset={1}
                        stopColor={inverted ? 'white' : 'black'}
                        stopOpacity={0}
                    />
                </linearGradient>
                <linearGradient
                    id={`gradient-${id}-2`}
                    x1="11.7555"
                    y1="4.8"
                    x2="11.7376"
                    y2="9.50002"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={inverted ? 'white' : 'black'} />
                    <stop
                        offset={1}
                        stopColor={inverted ? 'white' : 'black'}
                        stopOpacity={0}
                    />
                </linearGradient>
            </defs>
            <path
                d="M1 16L2.58314 11.2506C1.83084 9.74642 1.63835 8.02363 2.04013 6.39052C2.4419 4.75741 3.41171 3.32057 4.776 2.33712C6.1403 1.35367 7.81003 0.887808 9.4864 1.02289C11.1628 1.15798 12.7364 1.8852 13.9256 3.07442C15.1148 4.26363 15.842 5.83723 15.9771 7.5136C16.1122 9.18997 15.6463 10.8597 14.6629 12.224C13.6794 13.5883 12.2426 14.5581 10.6095 14.9599C8.97637 15.3616 7.25358 15.1692 5.74942 14.4169L1 16Z"
                fill={inverted ? 'black' : 'white'}
                stroke={inverted ? 'black' : 'white'}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <mask
                id="mask0_91_2047"
                style={{ maskType: 'alpha' }}
                maskUnits="userSpaceOnUse"
                x={1}
                y={0}
                width={16}
                height={16}
            >
                <circle
                    cx={9}
                    cy={8}
                    r={8}
                    fill={inverted ? 'black' : 'white'}
                />
            </mask>
            <g mask="url(#mask0_91_2047)">
                <circle
                    cx={9}
                    cy={8}
                    r={8}
                    fill={inverted ? 'black' : 'white'}
                />
                <path
                    d="M14.2896 14.0018L7.146 4.8H5.80005V11.1973H6.87681V6.16743L13.4444 14.6529C13.7407 14.4545 14.0231 14.2369 14.2896 14.0018Z"
                    fill={`url(#gradient-${id}-1)`}
                />
                <rect
                    x="11.2222"
                    y="4.8"
                    width="1.06667"
                    height="6.4"
                    fill={`url(#gradient-${id}-2)`}
                />
            </g>
        </svg>
    );
}

function IconOpenAI({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            fill="currentColor"
            viewBox="0 0 24 24"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <title>OpenAI icon</title>
            <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
        </svg>
    );
}

function IconVercel({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            aria-label="Vercel logomark"
            role="img"
            viewBox="0 0 74 64"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path
                d="M37.5896 0.25L74.5396 64.25H0.639648L37.5896 0.25Z"
                fill="currentColor"
            ></path>
        </svg>
    );
}

function IconGitHub({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            role="img"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <title>GitHub</title>
            <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
        </svg>
    );
}

function IconGoogle({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
            // width="64"
            // height="64"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <defs>
                <path
                    id="A"
                    d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"
                />
            </defs>
            <clipPath id="B">
                <use xlinkHref="#A" />
            </clipPath>
            <g transform="matrix(.727273 0 0 .727273 -.954545 -1.45455)">
                <path d="M0 37V11l17 13z" clipPath="url(#B)" fill="#fbbc05" />
                <path
                    d="M0 11l17 13 7-6.1L48 14V0H0z"
                    clipPath="url(#B)"
                    fill="#ea4335"
                />
                <path
                    d="M0 37l30-23 7.9 1L48 0v48H0z"
                    clipPath="url(#B)"
                    fill="#34a853"
                />
                <path
                    d="M48 48L17 24l-4-3 35-10z"
                    clipPath="url(#B)"
                    fill="#4285f4"
                />
            </g>
        </svg>
    );
}

function IconBitly({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            role="img"
            viewBox="-190 -190 1300 1300"
            xmlns="http://www.w3.org/2000/svg"
            fill="#ee6123"
            className={cn('h-8 w-8', className)}
            {...props}
        >
            <title>Bitly</title>
            <path d="m959.62 484.03c0 225.24-150.78 358.42-256.43 418.45-69.42 39.44-117.21 56.83-179.43 56.83-144.54 0-204.91-88.4-204.91-165.37 3.98-279.03-3.28-387.62-3.57-395.1-.38-9.73-.74-24.96-10.18-25.97-5.74-.62-9.44 1.14-16.04 9.38-9.89 13.19-29.85 18.41-42 11.36-14.67-8.52-17.41-27.33-9.98-44.64 20.6-46.15 53.63-66.57 98.15-65.93 76.91 1.1 98.98 51.52 98.98 114.51-1.14 63.62-1.71 112.47-1.71 146.53 27.2-22.25 67.18-46.12 133.95-46.12 92.23 0 171.73 67.96 171.73 193.41 0 73.7-19.24 111.55-19.38 111.91 93.32-81.42 145.02-209.07 145.02-314.79 0-173.03-134.42-377.17-385.79-377.17-206.51 0-389.84 173.73-389.84 380.2 0 114.96 59.77 230.05 146.23 306.07 22.28 19.59 23.27 47.55 11.28 59.16-14.21 13.78-39.02 15.25-59.84-.82-108.14-83.45-185.04-222.35-185.04-366.12 0-277.27 213.05-489.3 477.21-489.3 253.66 0 481.59 202.08 481.59 483.52zm-332.47 221.04c0-77.23-39.3-104.21-98.3-92.41-47.43 9.49-95.49 58.93-100.69 127.65-.86 11.34-1.15 30.13-.25 47.77 2.4 47.03 41.65 64.33 95.46 63.38 89.29-1.56 103.78-94.2 103.78-146.39z" />
        </svg>
    );
}

function IconBitlyText({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 209.7375 95.949997"
            height="95.949997"
            width="209.7375"
            version="1.1"
            id="svg2"
            fill="#ee4e1d"
            fillOpacity={1}
            fillRule="nonzero"
            className={cn('h-16 w-16', className)}
            {...props}
        >
            <metadata id="metadata8"></metadata>
            <defs id="defs6">
                <clipPath id="clipPath18" clipPathUnits="userSpaceOnUse">
                    <path
                        id="path20"
                        d="M 0,767.59 0,0 l 1677.95,0 0,767.59 -1677.95,0 z"
                    />
                </clipPath>
            </defs>
            <g transform="matrix(1.25,0,0,-1.25,0,95.95)" id="g10">
                <g transform="scale(0.1,0.1)" id="g12">
                    <g id="g14">
                        <g clipPath="url(#clipPath18)" id="g16">
                            <path
                                id="path22"
                                d="m 175.355,410.434 -3.597,-114.489 c 0,0 22.32,-28.8 72.008,-28.8 47.523,0 93.605,36.003 93.605,126.007 0,61.207 -27.359,97.215 -68.406,97.215 -64.086,0 -92.168,-44.648 -93.61,-79.933 z m -104.4058,265.703 0,2.164 c 0,4.32 -2.1601,9.359 -7.2031,9.359 -5.0391,0 -7.9219,-0.722 -13.6836,-7.918 -8.6367,-11.523 -21.6016,-15.125 -34.5586,-9.359 -12.96093,7.195 -19.44531,20.879 -12.96093,36 18.00003,40.32 48.96093,61.207 87.84763,61.207 69.1254,0 87.8474,-46.086 88.5624,-97.93 l 0,-2.16 c 0,-29.52 -3.598,-87.129 -3.598,-131.773 23.766,19.445 60.485,44.644 118.809,44.644 86.41,0 144.016,-79.203 144.016,-187.219 0,-42.476 -10.801,-77.043 -28.082,-103.683 65.527,28.797 110.886,91.445 110.886,215.297 0,20.16 15.84,36 36.004,36 20.164,0 36.004,-15.84 36.004,-36 0,-219.618 -151.215,-320.43 -349.226,-320.43 -126.012,0 -169.2191,79.211 -169.2191,115.211 0,21.601 3.6015,50.402 3.6015,72.008 0,208.816 -7.1992,266.425 -7.1992,304.582"
                            />
                            <path
                                id="path24"
                                d="m 521.656,285.148 c 0,25.2 3.602,50.403 3.602,86.407 0,61.203 -7.199,154.812 -7.199,154.812 0,29.524 24.48,54.004 54.004,54.004 29.519,0 54,-24.48 54,-54.004 0,-93.609 -7.196,-147.613 -7.196,-234.019 0,-13.68 11.52,-25.203 25.199,-25.203 46.809,0 97.207,61.207 97.207,237.621 0,20.16 15.844,36 36.008,36 20.16,0 36,-15.84 36,-36 0,-216.02 -75.605,-320.43 -194.414,-320.43 -55.445,0 -97.211,45.367 -97.211,100.812 z m 53.285,460.84 c 33.844,0 61.207,-27.363 61.207,-61.207 0,-33.843 -27.363,-61.207 -61.207,-61.207 -33.843,0 -61.207,27.364 -61.207,61.207 0,33.844 27.364,61.207 61.207,61.207"
                            />
                            <path
                                id="path26"
                                d="m 867.266,184.336 c -83.52,0 -129.606,49.687 -129.606,111.609 0,25.203 3.602,51.125 3.602,75.61 0,51.121 -0.719,95.769 -2.16,136.808 l -8.641,0 c -18.004,0 -32.402,14.407 -32.402,32.403 0,18.004 14.398,32.402 32.402,32.402 l 5.762,0 c -1.442,29.527 -2.164,56.887 -2.164,82.809 0,29.527 24.48,54.007 54.004,54.007 29.527,0 54.007,-24.48 54.007,-54.007 0,-22.321 -0.722,-49.684 -1.441,-80.645 23.043,0.719 49.684,1.441 84.254,1.441 20.156,0 35.996,-15.843 35.996,-36.007 0,-20.161 -15.84,-36 -35.996,-36 -35.293,0 -62.656,0 -86.414,0.718 -2.16,-59.043 -3.602,-123.129 -3.602,-180.734 0,-30.961 15.84,-57.605 50.406,-57.605 79.2,0 133.207,68.406 133.207,237.621 0,20.16 15.85,36 36.01,36 20.16,0 36,-15.84 36,-36 0,-198.02 -82.81,-320.43 -223.224,-320.43"
                            />
                            <path
                                id="path28"
                                d="m 1234.47,504.766 c 0,20.16 15.84,36 36,36 20.16,0 36,-15.84 36,-36 0,-216.02 -75.6,-320.43 -194.41,-320.43 -55.44,0 -97.21,45.367 -97.21,100.812 0,21.598 3.61,64.801 3.61,86.407 0,208.816 -7.21,280.824 -7.21,342.031 0,29.519 24.48,54.004 54,54.004 29.54,0 54.01,-24.485 54.01,-54.004 0,-61.207 -7.2,-126.012 -7.2,-421.238 0,-13.68 11.51,-25.203 25.21,-25.203 46.79,0 97.2,61.207 97.2,237.621"
                            />
                            <path
                                id="path30"
                                d="m 1492.95,168.496 c -81.37,-15.84 -132.5,-22.324 -132.5,-59.766 0,-16.562 12.25,-35.9995 50.41,-35.9995 64.8,0 84.25,39.5975 82.81,82.8045 0,4.32 -0.72,8.641 -0.72,12.961 z m -7.93,105.852 c -18.71,-23.043 -64.81,-43.203 -124.57,-43.203 -86.41,0 -126.01,61.203 -126.01,140.41 0,61.203 -7.19,108.011 -7.19,147.609 0,29.524 24.47,54.004 54,54.004 29.52,0 54,-24.48 54,-54.004 0,-39.598 -7.2,-97.207 -7.2,-140.41 0,-36.004 14.4,-64.805 57.6,-64.805 65.53,0 93.61,39.602 93.61,75.606 0,43.203 -7.19,90.011 -7.19,129.609 0,29.524 24.47,54.004 54,54.004 29.52,0 54.01,-24.48 54.01,-54.004 0,-39.598 -7.21,-86.406 -7.21,-147.609 l 0,-12.961 c 0.72,-36.723 4.32,-68.406 8.64,-95.774 12.96,7.203 25.93,20.887 42.49,44.645 8.64,12.242 23.04,16.562 36.72,10.086 14.4,-7.922 21.6,-21.606 14.4,-38.883 -15.84,-38.164 -46.08,-69.129 -84.96,-83.531 0.72,-4.317 0.72,-9.36 0.72,-14.399 C 1590.88,97.9297 1563.52,0 1410.86,0 c -115.22,0 -147.62,51.1289 -147.62,108.73 0,91.45 115.94,97.93 225.38,126.012 l -3.6,39.606"
                            />
                        </g>
                    </g>
                </g>
            </g>
        </svg>
    );
}

function IconBilly({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            version="1.1"
            id="Layer_1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            viewBox="0 0 1920 1080"
            className={cn('h-16 w-16', className)}
            xmlSpace="preserve"
            {...props}
        >
            <style jsx>{`
                .st0 {
                    fill: #ffffff;
                }
                .st1 {
                    fill: #4dab64;
                }
                .st2 {
                    fill: #65bf77;
                }
                .st3 {
                    fill: #6ac07a;
                }
                .st4 {
                    fill: #449c5b;
                }
                .st5 {
                    fill: #469e5c;
                }
                .st6 {
                    fill: #63be76;
                }
                .st7 {
                    fill: #47a15e;
                }
                .st8 {
                    fill: #419758;
                }
                .st9 {
                    fill: #6dc17c;
                }
                .st10 {
                    fill: #60be74;
                }
                .st11 {
                    fill: #48a25f;
                }
                .st12 {
                    fill: #50ae66;
                }
                .st13 {
                    fill: #52b168;
                }
                .st14 {
                    fill: #54b56c;
                }
                .st15 {
                    fill: #439a59;
                }
                .st16 {
                    fill: #49a560;
                }
                .st17 {
                    fill: #52b36a;
                }
                .st18 {
                    fill: #5dbb71;
                }
                .st19 {
                    fill: #56b76e;
                }
                .st20 {
                    fill: #5ab96f;
                }
                .st21 {
                    fill: #51b068;
                }
                .st22 {
                    fill: #5ab970;
                }
                .st23 {
                    fill: #4ba862;
                }
                .st24 {
                    fill: #409556;
                }
                .st25 {
                    fill: #52b46a;
                }
                .st26 {
                    fill: #3f9455;
                }
                .st27 {
                    fill: #d0e5d0;
                }
                .st28 {
                    fill: #d0e4cf;
                }
                .st29 {
                    fill: #a4d5a7;
                }
                .st30 {
                    fill: #85b087;
                }
                .st31 {
                    fill: #b7ddb8;
                }
                .st32 {
                    fill: #9ebe9d;
                }
                .st33 {
                    fill: #e5f1e5;
                }
                .st34 {
                    fill: #e4efe4;
                }
                .st35 {
                    fill: #a0c8a2;
                }
                .st36 {
                    fill: #7eb786;
                }
                .st37 {
                    fill: #7db685;
                }
                .st38 {
                    fill: #59b86f;
                }
                .st39 {
                    fill: #51b26a;
                }
                .st40 {
                    fill: #ea4f24;
                }
                .st41 {
                    fill: url(#SVGID_1_);
                }
            `}</style>
            <g>
                <path
                    className="st40"
                    d="M962.5,288.8c0.3-17.1,0.8-34.3,1-51.4c0.3-27-15.9-50.1-41.1-58.9c-34.1-11.9-72.8,10.5-78.4,46.2
		c-1.7,10.9-0.9,22.3-1,33.5c-0.1,8,0,16.1,0.2,24.1c0.3,9.7,0.6,18,1.3,31.7c0.3,5.3,0.5,9.4,0.6,11c0,0.1,0,0.5,0,0.6
		c4,74.3,3.9,105.9,3.9,105.9c0,19.9-0.6,33-1.3,43.7c0,0-0.7,11.6-1.9,22c-3.8,32.7-9.8,64.9-21.6,95.7
		c-7.3,19.2-16.6,37.3-30.7,52.5c-11,11.9-23.9,20.8-40.1,23.9c-10.1,2-20,1.9-28.5-5c-6.9-5.6-10.8-12.8-10.9-21.7
		c-0.1-14.6-0.2-29.1,0-43.7c0.2-12.2,0.8-24.4,1.3-36.6c0.4-9.8,0.8-19.6,1.3-29.4c0.6-12.9,1.3-25.8,1.9-38.8
		c0.5-9.8,1-19.6,1.3-29.5c0.8-27.8,1.8-55.7,2-83.5c0.2-23.9-11.1-41.7-31.6-53.6c-7.6-4.4-15.9-6.3-24.8-7
		c-30.2-2.4-53.3,18.6-60.7,39.7c-3.7,10.6-4,21.3-2.7,32.3c0.8,6.5,0.8,13.1,1.1,19.7c0.2,3.2,0.8,6.5,0.6,9.7
		c-0.7,12.4-1.7,24.8-2.5,37.2c-1,15.5-4.1,30.7-7.9,45.8c-8.5,33.7-22.4,64.7-45.3,91.3c-18.3,21.1-40.4,37.1-65.9,48.3l-0.8,0.1
		c0.1-0.2,0.2-0.5,0.3-0.7c5.6-11.5,11.9-22.7,16.6-34.5c8.2-20.9,12.4-42.9,12.9-65.4c0.2-8,0.8-16,0.5-24
		c-1.4-42.6-9.8-83.5-31.2-120.9c-16.5-28.9-38.9-51.8-69.4-66c-12.6-5.9-25.8-9.8-39.5-11.5c-8.5-1-17.2-1.5-25.8-1.4
		c-46.7,0.8-87.6,17.3-123.6,46.5c-1.1,0.9-2.3,1.8-4,3.1c0-1.6,0-2.7,0-3.7c0.1-9.1,0-18.3,0.2-27.4c0.3-13.3,0.9-26.6,1.3-39.9
		c0.3-8.8,0.3-17.5,0.6-26.3c0.6-16.9,1.6-33.8,1.7-50.6c0.1-19.2-2.1-38.2-9-56.3c-11.6-30.5-34-47.6-65.8-53
		c-4.8-0.8-9.6-1.3-14.4-2h-18.3c-0.8,0.2-1.6,0.5-2.5,0.6c-16.5,1.5-31.8,6.6-45.6,15.9c-20,13.5-33.4,32.3-42.5,54.3
		c-1.2,2.9-1.8,6.3-1.9,9.5c-0.6,12.4,7.6,24.7,19.1,29.2c12.8,4.9,26,1.5,35-9.1c1.9-2.2,3.8-4.5,5.8-6.6c2.8-2.9,6.2-4.3,10.2-4.4
		c5,0,8.7,3.4,8.8,8.4c0.2,5.6,0.3,11.2,0.5,16.8c0.4,10.6,0.8,21.1,1.3,31.7c0.4,8.9,0.9,17.9,1.3,26.8c0.4,9.6,0.9,19.2,1.3,28.8
		c0.5,12.9,0.9,25.8,1.3,38.7c0.2,7.4,0.4,14.7,0.6,22.1c0.3,11.2,0.5,22.3,0.7,33.5c0.4,25,0.9,50,1,75c0.1,31.7,1.6,63.3-0.9,95
		c-0.8,9.6-1.5,19.2-1.9,28.8c-0.4,8.5-0.4,17.1-0.8,25.6c-0.4,9.1,1.3,17.9,4.3,26.4c9.1,26.6,25.9,47.7,48.1,64.7
		c35.5,27.2,76.8,37.9,120.5,40.2c22.1,1.2,44.4,0.7,66.5-1.7c13-1.4,26-2.7,39-4.6c27-4,53.4-10.4,79.2-19.3
		c54.1-18.7,101.7-47.5,140.2-90.4c0.5-0.6,1.1-1,1.7-1.5c0.3,5.2,0.2,10.2,1,15.1c1.5,9.1,2.5,18.4,5.4,27
		c12.4,36.6,36.9,61.2,74.4,72.1c18.1,5.2,36.5,4.5,54.9,2.2c42.2-5.5,78-23.6,107.1-54.7c4-4.3,7.7-8.7,11.7-13.2
		c0.6,1,1,1.8,1.5,2.7c7.3,13.8,17.1,25.6,29.3,35.4c25.4,20.4,55.3,28.8,87,31.1c17,1.2,34,0.3,51-2.3
		c45.7-6.8,85.2-26.1,118.1-58.6c5.4-5.3,10.4-11,15.7-16.5c0.1,0.2,0.3,0.4,0.4,0.5c0.2,0.7,0.4,1.5,0.7,2.2
		c13.4,37.1,38.5,61.8,77,71.9c13.7,3.6,27.7,3.8,41.7,2.4c8.8-0.9,17.7-1.9,26.3-3.7c44-9.3,78.8-33.2,105.5-69
		c6.9-9.3,13-19.2,19.4-28.9c0.2,0.2,0.6,0.7,1,1.2c22.3,29,52.6,43.5,88.3,47.6c17.9,2.1,35.8,1.5,53.7-0.6
		c20.9-2.5,41.3-7.1,60.7-15.2c16.4-6.8,31.6-15.5,43.7-28.7c0.6-0.7,1-1.6,1.5-2.4c0.3,0.2,0.5,0.4,0.8,0.7
		c1.3,14.4,2.5,28.9,3.8,43.5c-1,0.3-1.9,0.6-2.8,0.8c-23.8,5.5-47.6,11-71.4,16.4c-27.9,6.3-55.9,12.4-83.1,21.6
		c-20,6.7-39.4,14.8-56.6,27.3c-25.6,18.5-37.9,43.7-38.3,74.9c-0.1,7.9,0.8,15.8,2.1,23.6c6.1,38,27.2,64.5,62,80.3
		c16.6,7.5,34.1,12,52.1,14.4c9,1.2,18.1,2.9,27.1,3c18.4,0.3,36.8,0.9,55.2-1.1c18.6-2.1,37-5.2,54.8-11.2
		c38.6-12.9,69-36.1,88.5-72.3c15.7-29,22.1-60.5,23.9-93.1c0.7-13.3,0.1-26.7,0.1-40.1c0.6-0.3,1.1-0.5,1.6-0.7
		c43.9-17.5,74.1-48.9,93-91.7c3-6.8,4.3-13.8,3.1-21c-3.3-19.9-27.1-32.4-45.6-24.2c-7.6,3.3-12.5,9.5-17.1,16
		c-9.4,13.2-19.2,26.1-31.7,36.7c-3.9,3.3-8.3,6.2-12.9,9.7c-1.3-9.2-2.7-17.4-3.5-25.7c-2.1-22.1-4.5-44.2-5.5-66.4
		c-0.9-19.2-0.3-38.5-0.2-57.7c0.1-9.6,0.7-19.3,1.2-28.9c0.4-7,0.8-14,1.3-20.9c1.1-15.4,2.2-30.9,3.3-46.3
		c0.4-6,0.5-11.9,0.6-17.9c0.3-9.7,1.5-19.6,0.6-29.2c-3.9-41.4-48.2-67.5-86.8-47.9c-22.2,11.2-33,30.4-33.7,55
		c-0.3,9.1,0.6,18.3,1.2,27.5c1,14.9,2.2,29.8,3.2,44.7c0.9,11.9,1.9,23.8,2.6,35.8c0.5,9.9,0.5,19.9,0.7,29.9
		c0.1,5.5,0.5,11-0.2,16.3c-2.9,20.6-12.5,37.5-28.5,50.7c-19.3,15.9-42.4,21.7-66.7,23.2c-8,0.5-16.3-0.1-24.2-1.3
		c-19.9-3.1-34.3-13.9-42.2-32.7c-4.1-9.8-6.1-20.2-6.4-30.7c-0.6-20.1,0.1-40.2,1.8-60.3c1.1-12.5,1.8-25.1,2.6-37.6
		c1-15,1.9-30,2.7-45c0.2-3.4,0-6.7,0-10.1c0-7,0.8-14.1-0.2-20.9c-4.6-29.7-31.9-55-68-50.4c-15.1,1.9-27.7,9.3-37.5,20.9
		c-9.3,11-14.4,23.6-14.3,38.1c0,9.3,0.3,18.6,0.7,27.9c0.4,8.2,1.1,16.4,1.6,24.6c0.7,11.1,2.3,22.1,2,33.2
		c-0.3,12.7-1.6,25.4-3.3,38.1c-3.1,23.5-8,46.6-15.5,69.1c-6.7,20.1-15.1,39.3-28,56.3c-10.4,13.7-22.8,24.7-39.4,30.3
		c-7.5,2.5-15.2,3.9-23.1,2.7c-14.5-2.2-24.1-14.3-24.1-29.9c0.3-71.8,0.7-143.6,1.1-215.3c0-4.9,0.5-9.7,0.6-14.5
		c0.5-20,0.8-39.9,1.3-59.9c0.2-9.5,0.4-19,0.7-28.6c0.2-6.2,0.4-12.3,0.6-18.5c0.3-9.1,0.5-18.2,0.8-27.3c0.1-4.5,0.4-9.1,0.5-13.6
		c0.4-12.5,0.9-25.1,1.3-37.6c0.2-6.9,0.5-13.8,0.6-20.7c0.2-10.9,0.8-21.7,0.5-32.6c-0.7-25.9-13-44.6-36.3-55.6
		c-5.5-2.6-11.7-3.6-17.6-5.3h-13.7c-4.9,1.4-10.1,2.4-14.8,4.4c-23.9,10.4-37.1,28.7-38.2,54.9c-0.5,13.7-0.1,27.4,0.1,41.1
		c0.2,11.8,0.8,23.5,1.3,35.3c0.4,11.7,0.9,23.3,1.3,35c0.4,12.1,0.9,24.2,1.3,36.3c0.3,7.5,0.5,14.9,0.7,22.4
		c0.5,19.5,0.9,39,1.3,58.5c0.3,16.8,1.3,33.6,0.5,50.4c-2,41.5-7.7,82.5-22,121.8c-8.2,22.6-19.3,43.7-35.8,61.5
		c-20.3,21.9-45.3,34.2-74.9,37.3c-11.6,1.2-23.2,1.6-34.4-2.3c-19.2-6.6-29.6-21-34.1-39.9c-1.8-7.4-2.6-15.3-2.6-22.9
		c0.1-35.9,0.7-71.7,1.2-107.6c0-2.2-0.1-4.3-0.1-6.5c0.2-11.2,0.4-22.3,0.7-33.5c0.6-22.2,0.4-17.4,1.3-39.5 M390.3,593.3
		c-6.9,22.7-18.3,42.6-37.4,57.5c-20.5,16-44,21.3-69.5,19.3c-19.5-1.5-37.6-7.1-53.8-18.1c-4.6-3.2-8.6-7.2-13.1-10.6
		c-3.5-2.6-3.1-6.1-2.8-9.6c0.1-1.3,0.5-2.5,0.5-3.8c0.3-11.8,0.4-23.6,0.7-35.4c0.4-14.3,0.8-28.5,1.3-42.8
		c0.2-6.4,0.2-12.8,0.7-19.1c1-14.2,0.2-28.6,4.9-42.4c11.8-35.1,36.4-55.9,72-64.1c8.7-2,17.8-2.5,26.7-3.6c0,0.3,0.1,0.6,0.1,0.9
		c37.9-1.8,59.2,24.4,68.6,48.9c4.6,12,7.3,24.4,8.6,37.2C400.6,536.5,398.8,565.2,390.3,593.3z M1691.6,811.4
		c-1.5,20.4-7.9,38.9-22.9,53.5c-11.8,11.5-26.4,17.9-42.5,20.4c-10.5,1.7-21.3,2.7-31.9,2.6c-11.7-0.2-23.2-2.7-33.4-9.1
		c-12.8-8.1-19-20-17.7-34.9c1.1-13.3,8.9-22.7,20.1-29.5c13.5-8.1,28.3-12.8,43.4-16.2c27-5.9,54.2-11.3,81.3-16.8
		c0.9-0.2,1.9-0.2,3.8-0.3C1691.8,791.5,1692.4,801.5,1691.6,811.4z"
                />
                <path
                    className="st0"
                    d="M482,644.7c-0.3,0-0.5,0.1-0.8,0.1c0.1-0.2,0.2-0.5,0.3-0.7C481.7,644.3,481.8,644.5,482,644.7z"
                />
                <path
                    className="st40"
                    d="M732.8,203.6c0.9,34.7-26.5,65.9-63,68.2c-44.4,2.8-73.7-32.8-73.5-68.3c0.2-37.4,29.6-64.9,60.8-68
		C704.8,130.8,734.8,170.9,732.8,203.6z"
                />
            </g>
        </svg>
    );
}

function IconSeparator({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            fill="none"
            shapeRendering="geometricPrecision"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1"
            viewBox="0 0 24 24"
            aria-hidden="true"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M16.88 3.549L7.12 20.451"></path>
        </svg>
    );
}

function IconArrowDown({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="m205.66 149.66-72 72a8 8 0 0 1-11.32 0l-72-72a8 8 0 0 1 11.32-11.32L120 196.69V40a8 8 0 0 1 16 0v156.69l58.34-58.35a8 8 0 0 1 11.32 11.32Z" />
        </svg>
    );
}

function IconArrowRight({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="m221.66 133.66-72 72a8 8 0 0 1-11.32-11.32L196.69 136H40a8 8 0 0 1 0-16h156.69l-58.35-58.34a8 8 0 0 1 11.32-11.32l72 72a8 8 0 0 1 0 11.32Z" />
        </svg>
    );
}

function IconUser({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M230.92 212c-15.23-26.33-38.7-45.21-66.09-54.16a72 72 0 1 0-73.66 0c-27.39 8.94-50.86 27.82-66.09 54.16a8 8 0 1 0 13.85 8c18.84-32.56 52.14-52 89.07-52s70.23 19.44 89.07 52a8 8 0 1 0 13.85-8ZM72 96a56 56 0 1 1 56 56 56.06 56.06 0 0 1-56-56Z" />
        </svg>
    );
}

function IconPlus({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M224 128a8 8 0 0 1-8 8h-80v80a8 8 0 0 1-16 0v-80H40a8 8 0 0 1 0-16h80V40a8 8 0 0 1 16 0v80h80a8 8 0 0 1 8 8Z" />
        </svg>
    );
}

function IconArrowElbow({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M200 32v144a8 8 0 0 1-8 8H67.31l34.35 34.34a8 8 0 0 1-11.32 11.32l-48-48a8 8 0 0 1 0-11.32l48-48a8 8 0 0 1 11.32 11.32L67.31 168H184V32a8 8 0 0 1 16 0Z" />
        </svg>
    );
}

function IconSpinner({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4 animate-spin', className)}
            {...props}
        >
            <path d="M232 128a104 104 0 0 1-208 0c0-41 23.81-78.36 60.66-95.27a8 8 0 0 1 6.68 14.54C60.15 61.59 40 93.27 40 128a88 88 0 0 0 176 0c0-34.73-20.15-66.41-51.34-80.73a8 8 0 0 1 6.68-14.54C208.19 49.64 232 87 232 128Z" />
        </svg>
    );
}

function IconMessage({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M216 48H40a16 16 0 0 0-16 16v160a15.84 15.84 0 0 0 9.25 14.5A16.05 16.05 0 0 0 40 240a15.89 15.89 0 0 0 10.25-3.78.69.69 0 0 0 .13-.11L82.5 208H216a16 16 0 0 0 16-16V64a16 16 0 0 0-16-16ZM40 224Zm176-32H82.5a16 16 0 0 0-10.3 3.75l-.12.11L40 224V64h176Z" />
        </svg>
    );
}

function IconTrash({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M216 48h-40v-8a24 24 0 0 0-24-24h-48a24 24 0 0 0-24 24v8H40a8 8 0 0 0 0 16h8v144a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16V64h8a8 8 0 0 0 0-16ZM96 40a8 8 0 0 1 8-8h48a8 8 0 0 1 8 8v8H96Zm96 168H64V64h128Zm-80-104v64a8 8 0 0 1-16 0v-64a8 8 0 0 1 16 0Zm48 0v64a8 8 0 0 1-16 0v-64a8 8 0 0 1 16 0Z" />
        </svg>
    );
}

function IconRefresh({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M197.67 186.37a8 8 0 0 1 0 11.29C196.58 198.73 170.82 224 128 224c-37.39 0-64.53-22.4-80-39.85V208a8 8 0 0 1-16 0v-48a8 8 0 0 1 8-8h48a8 8 0 0 1 0 16H55.44C67.76 183.35 93 208 128 208c36 0 58.14-21.46 58.36-21.68a8 8 0 0 1 11.31.05ZM216 40a8 8 0 0 0-8 8v23.85C192.53 54.4 165.39 32 128 32c-42.82 0-68.58 25.27-69.66 26.34a8 8 0 0 0 11.3 11.34C69.86 69.46 92 48 128 48c35 0 60.24 24.65 72.56 40H168a8 8 0 0 0 0 16h48a8 8 0 0 0 8-8V48a8 8 0 0 0-8-8Z" />
        </svg>
    );
}

function IconStop({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm0 192a88 88 0 1 1 88-88 88.1 88.1 0 0 1-88 88Zm24-120h-48a8 8 0 0 0-8 8v48a8 8 0 0 0 8 8h48a8 8 0 0 0 8-8v-48a8 8 0 0 0-8-8Zm-8 48h-32v-32h32Z" />
        </svg>
    );
}

function IconSidebar({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M216 40H40a16 16 0 0 0-16 16v144a16 16 0 0 0 16 16h176a16 16 0 0 0 16-16V56a16 16 0 0 0-16-16ZM40 56h40v144H40Zm176 144H96V56h120v144Z" />
        </svg>
    );
}

function IconSettings({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            // height="800px"
            // width="800px"
            version="1.1"
            id="Layer_1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            xmlSpace="preserve"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <g>
                <path
                    fill="#F1B14F"
                    d="M256,512C114.617,512,0,397.383,0,256S114.617,0,256,0s256,114.617,256,256S397.383,512,256,512
		L256,512z"
                />
                <path
                    fill="#F0973E"
                    d="M319.592,290.925c-2.48,0-4.898,0.264-7.234,0.77c-2.615,0.568-5.208,1.462-7.725,2.687
		c-1.199,0.584-2.346,1.23-3.441,1.927c-4.154,2.666-7.699,6.206-10.37,10.376c-2.599,4.072-4.304,8.655-5.012,13.419
		c-0.196,1.338-0.315,2.697-0.351,4.082c-0.072,2.837,0.202,5.601,0.78,8.252c0.574,2.584,1.452,5.136,2.661,7.616
		c0.579,1.183,1.214,2.32,1.902,3.4c2.687,4.206,6.278,7.792,10.5,10.474c4.077,2.578,8.655,4.268,13.419,4.96
		c1.307,0.186,2.635,0.3,3.984,0.336l0.909,0.01c2.434,0,4.811-0.258,7.1-0.744c2.661-0.568,5.301-1.467,7.854-2.713
		c1.209-0.589,2.372-1.245,3.477-1.953c4.144-2.666,7.684-6.206,10.345-10.365c2.589-4.067,4.294-8.639,5.002-13.393
		c0.196-1.338,0.315-2.708,0.351-4.092c0.072-2.832-0.196-5.591-0.78-8.242c-0.574-2.584-1.452-5.141-2.666-7.627
		c-0.584-1.199-1.23-2.351-1.933-3.452c-2.702-4.206-6.299-7.792-10.536-10.469c-4.01-2.527-8.51-4.196-13.192-4.898
		c-1.359-0.202-2.744-0.32-4.149-0.362H319.592L319.592,290.925z M112.426,200.681H92.223l0,0l31.571,30.269
		c-1.483-2.382-2.857-4.867-4.113-7.446C116.069,216.099,113.677,208.416,112.426,200.681L112.426,200.681z M193.126,153.51
		c-5.022,0-10.128,1.116-14.949,3.467c-12.122,5.911-19.175,18.049-19.196,30.682c-0.005,5.038,1.106,10.159,3.462,14.995
		c5.922,12.138,18.075,19.196,30.729,19.196c5.022,0,10.128-1.116,14.949-3.462c12.122-5.911,19.175-18.049,19.196-30.682
		c0.005-5.038-1.106-10.159-3.462-14.995C217.934,160.563,205.776,153.505,193.126,153.51L193.126,153.51z M273.713,125.509
		l-14.292,14.292c2.697,3.741,5.11,7.761,7.198,12.045c3.612,7.404,6.004,15.093,7.255,22.823h6.242h13.956v26.011h-20.214
		c-2.031,12.541-6.96,24.513-14.437,34.873l7.968,7.968l6.319,6.319l-18.395,18.395l-14.292-14.292
		c-3.741,2.697-7.761,5.11-12.045,7.198c-7.41,3.612-15.088,6.004-22.823,7.255v8.841v11.357h-26.011v-20.219
		c-12.541-2.031-24.508-6.96-34.873-14.437l-0.656,0.656l-13.631,13.631l-18.395-18.395l0,0l113.15,113.15l-6.294-19.966
		l19.268-6.077c-1.876-12.768-0.656-25.639,3.312-37.596l-17.94-9.337l12.014-23.071l17.925,9.332
		c7.379-9.931,17.129-18.333,28.92-24.249l-6.082-19.289l24.807-7.823l6.077,19.268c3.937-0.579,7.885-0.863,11.812-0.863
		c8.81,0,17.517,1.431,25.789,4.175l9.337-17.94l23.071,12.014l-9.337,17.935c9.931,7.379,18.333,17.129,24.244,28.92l19.289-6.082
		l7.823,24.807l-18.323,5.777l0,0l-0.946,0.3c1.876,12.768,0.656,25.639-3.312,37.596l17.94,9.337l-12.014,23.071l-17.925-9.332
		c-7.379,9.931-17.129,18.333-28.92,24.249l6.082,19.289l-24.807,7.823l-2.708-8.593l0,0l-3.364-10.675
		c-3.937,0.579-7.885,0.863-11.812,0.863c-8.81,0-17.517-1.431-25.789-4.175l-9.337,17.94l-23.071-12.014l6.469-12.427l0,0
		l2.863-5.498c-9.931-7.379-18.333-17.129-24.249-28.92l-0.904,0.284l-18.385,5.798l36.563,36.237l-2.356,4.526l86.735,86.219
		c67.576-26.104,121.272-80.023,147.067-147.749L273.713,125.509L273.713,125.509z M112.592,125.509L112.592,125.509l6.159,6.159
		L112.592,125.509L112.592,125.509z M141.827,117.96l3.446,3.446l0,0L141.827,117.96L141.827,117.96z M158.801,113.501
		c-0.496,0.227-0.987,0.465-1.478,0.703l-0.45,0.222l0.45-0.222C157.809,113.966,158.305,113.734,158.801,113.501L158.801,113.501z
		 M206.158,86.751v20.219c10.67,1.731,20.932,5.56,30.14,11.264L206.158,86.751L206.158,86.751z"
                />
                <path
                    fill="#2B8199"
                    d="M231.322,174.669c1.39,4.087,2.181,8.448,2.181,13.006s-0.791,8.918-2.181,13.006h62.75v-26.011
		L231.322,174.669L231.322,174.669z M180.147,149.5c4.087-1.39,8.448-2.181,13.006-2.181s8.918,0.791,13.006,2.181v-62.75h-26.011
		L180.147,149.5L180.147,149.5z M229.332,169.885l44.38-44.38l-18.395-18.395l-44.38,44.38
		C218.921,155.427,225.4,161.902,229.332,169.885L229.332,169.885z M152.797,187.675c0-4.557,0.791-8.918,2.181-13.006h-62.75
		v26.011h62.75C153.582,196.594,152.797,192.233,152.797,187.675L152.797,187.675z M175.362,151.495l-44.38-44.38l-18.395,18.395
		l44.38,44.38C160.899,161.902,167.379,155.427,175.362,151.495L175.362,151.495z M156.967,205.466l-44.38,44.38l18.395,18.395
		l44.38-44.38C167.379,219.923,160.899,213.449,156.967,205.466L156.967,205.466z M210.938,223.855l44.38,44.38l18.395-18.395
		l-44.38-44.38C225.4,213.449,218.921,219.923,210.938,223.855L210.938,223.855z M180.147,225.85v62.75h26.011v-62.75
		c-4.087,1.39-8.448,2.186-13.006,2.186C188.595,228.03,184.229,227.24,180.147,225.85L180.147,225.85z"
                />
                <polygon
                    fill="#A27E36"
                    points="145.274,253.949 144.612,254.605 144.612,254.605 145.274,253.949 	"
                />
                <path
                    fill="#1D6B86"
                    d="M139.988,249.799c1.483,1.674,3.023,3.276,4.625,4.811l0.656-0.661
		C143.46,252.641,141.698,251.257,139.988,249.799L139.988,249.799z"
                />
                <path
                    fill="#1D6B86"
                    d="M280.115,174.669h-6.242c1.411,8.717,1.364,17.491-0.016,26.011h8.82
		C283.215,192.057,282.42,183.288,280.115,174.669L280.115,174.669z"
                />
                <path
                    fill="#1D6B86"
                    d="M259.421,235.554c-5.022,6.96-11.187,13.197-18.395,18.39l7.921,7.921
		c7.208-5.193,13.424-11.378,18.447-18.343L259.421,235.554L259.421,235.554z"
                />
                <path
                    fill="#1D6B86"
                    d="M180.147,268.386v6.206c6.872,1.829,13.982,2.801,21.165,2.801c1.612,0,3.229-0.047,4.847-0.15
		v-8.841c-4.309,0.698-8.645,1.039-12.944,1.039C188.817,269.435,184.446,269.078,180.147,268.386L180.147,268.386z"
                />
                <path
                    fill="#1D6B86"
                    d="M255.013,134.216c1.54,1.783,3.012,3.643,4.408,5.581l0.708-0.708
		C258.491,137.383,256.78,135.756,255.013,134.216L255.013,134.216z"
                />
                <path
                    fill="#3194B0"
                    d="M157.318,114.204c-40.577,19.79-57.427,68.723-37.637,109.3s68.723,57.427,109.3,37.637
		s57.427-68.728,37.637-109.3C246.834,111.269,197.896,94.414,157.318,114.204L157.318,114.204z M208.127,218.378
		c-16.959,8.267-37.405,1.23-45.677-15.729c-8.267-16.959-1.23-37.405,15.729-45.677s37.41-1.23,45.677,15.729
		C232.123,189.659,225.08,210.111,208.127,218.378L208.127,218.378z"
                />
                <path
                    fill="#2B8199"
                    d="M171.223,142.716c-24.833,12.112-35.142,42.055-23.035,66.888
		c12.112,24.833,42.055,35.142,66.888,23.035c24.833-12.112,35.142-42.055,23.035-66.888
		C226,140.918,196.056,130.604,171.223,142.716L171.223,142.716z M208.127,218.378c-16.959,8.267-37.405,1.23-45.677-15.729
		c-8.267-16.959-1.23-37.405,15.729-45.677s37.41-1.23,45.677,15.729C232.123,189.659,225.08,210.111,208.127,218.378
		L208.127,218.378z"
                />
                <path
                    fill="#CEEAF2"
                    d="M131.989,176.788c-1.395,0.677-3.023,0.878-4.635,0.45c-3.56-0.951-5.679-4.604-4.728-8.159
		c5.472-20.539,19.563-37.663,38.66-46.974c3.312-1.612,7.301-0.238,8.918,3.069c1.612,3.307,0.243,7.301-3.069,8.918
		c-15.615,7.616-27.143,21.619-31.618,38.423C134.996,174.452,133.668,175.966,131.989,176.788z"
                />
                <path
                    fill="#217794"
                    d="M143.202,190.615c0.45,7.746,2.671,15.016,6.252,21.413c-0.439-0.796-0.863-1.602-1.266-2.429
		C145.201,203.471,143.574,197.028,143.202,190.615L143.202,190.615z M155.324,154.926c-7.606,8.779-12.21,20.224-12.21,32.749
		l0.01,0.946C142.902,176.313,147.248,164.258,155.324,154.926L155.324,154.926z M190.946,137.688
		c-11.833,0.512-22.596,5.136-30.91,12.479c3.302-2.909,7.043-5.431,11.187-7.451C177.589,139.611,184.291,137.978,190.946,137.688
		L190.946,137.688z M191.003,137.688h-0.036H191.003L191.003,137.688z M193.152,137.642l-1.059,0.01l1.023-0.01
		c8.262,0,16.38,2.057,23.609,5.911C209.703,139.781,201.678,137.642,193.152,137.642L193.152,137.642z"
                />
                <path
                    fill="#1D6781"
                    d="M193.116,137.642l-1.023,0.01l-1.09,0.036h-0.036h-0.021c-6.655,0.289-13.352,1.922-19.718,5.028
		c-4.149,2.02-7.885,4.542-11.187,7.451c-1.674,1.478-3.25,3.069-4.712,4.759c-8.076,9.332-12.422,21.387-12.2,33.695
		c0.01,0.672,0.036,1.333,0.077,2c0.377,6.412,2,12.856,4.986,18.984c0.403,0.827,0.827,1.633,1.266,2.429
		c3.204,5.72,7.497,10.742,12.597,14.799c-6.795-8.541-10.882-19.335-10.882-31.096c0-27.634,22.399-50.033,50.033-50.033
		c11.76,0,22.555,4.087,31.096,10.882c-4.242-5.332-9.539-9.781-15.584-13.026C209.496,139.698,201.373,137.642,193.116,137.642
		L193.116,137.642z"
                />
                <path
                    fill="#CEEAF2"
                    d="M359.477,331.177c-0.651,4.268-1.964,8.505-4.072,12.546s-4.821,7.549-7.942,10.531l55.66,28.977
		l12.014-23.071L359.477,331.177L359.477,331.177z M325.699,285.22c4.268,0.651,8.5,1.964,12.546,4.072
		c4.046,2.108,7.549,4.821,10.531,7.942l28.977-55.66l-23.071-12.014L325.699,285.22L325.699,285.22z M359.916,326.015l59.856-18.87
		l-7.823-24.807l-59.856,18.87C357.358,308.379,360.118,317.117,359.916,326.015L359.916,326.015z M283.815,306.452
		c2.103-4.041,4.821-7.549,7.942-10.531l-55.66-28.977l-12.014,23.071l55.66,28.977C280.394,314.73,281.712,310.492,283.815,306.452
		L283.815,306.452z M320.537,284.776l-18.87-59.856l-24.807,7.823l18.87,59.856C302.902,287.333,311.64,284.579,320.537,284.776
		L320.537,284.776z M279.304,324.154l-59.856,18.87l7.823,24.807l59.856-18.87C281.856,341.79,279.102,333.052,279.304,324.154
		L279.304,324.154z M318.682,365.393l18.876,59.856l24.807-7.823L343.49,357.57C336.313,362.835,327.58,365.59,318.682,365.393
		L318.682,365.393z M290.444,352.94l-28.977,55.66l23.071,12.014l28.977-55.66c-4.268-0.651-8.505-1.964-12.546-4.072
		C296.934,358.779,293.425,356.061,290.444,352.94L290.444,352.94z"
                />
                <polygon
                    fill="#2E582B"
                    points="246.555,361.756 245.65,362.04 246.555,361.756 	"
                />
                <path
                    fill="#2888A7"
                    d="M243.744,355.524c0.548,2.206,1.183,4.382,1.907,6.516l0.904-0.284l-0.413-0.842
		C245.268,359.136,244.472,357.338,243.744,355.524L243.744,355.524z"
                />
                <path
                    fill="#A6D7E6"
                    d="M397.197,350.812c-2.733,8.242-6.769,16.044-11.998,23.082l7.813,4.067
		c4.459-7.399,7.802-15.548,9.74-24.254L397.197,350.812L397.197,350.812z"
                />
                <polygon
                    fill="#C28B3B"
                    points="331.481,405.981 331.481,405.981 334.845,416.656 	"
                />
                <path
                    fill="#A6D7E6"
                    d="M356.278,398.137l-0.842,0.413c-7.771,3.788-15.842,6.237-23.96,7.43l3.364,10.675
		c8.789-1.281,17.16-3.896,24.828-7.756L356.278,398.137L356.278,398.137z"
                />
                <path
                    fill="#A6D7E6"
                    d="M270.804,390.676l-2.863,5.498c6.428,5.875,13.77,10.768,21.847,14.365l4.092-7.864
		C285.644,399.941,277.836,395.905,270.804,390.676L270.804,390.676z"
                />
                <path
                    fill="#A6D7E6"
                    d="M399.16,306.214c0.553,2.325,1.002,4.661,1.343,7.001l0.946-0.3
		C400.788,310.637,400.023,308.405,399.16,306.214L399.16,306.214z"
                />
                <path
                    fill="#CEEAF2"
                    d="M283.778,251.613c-40.577,19.79-57.427,68.723-37.637,109.3s68.728,57.427,109.3,37.637
		c40.577-19.79,57.427-68.728,37.637-109.3S324.356,231.823,283.778,251.613L283.778,251.613z M334.582,355.788
		c-16.959,8.267-37.41,1.23-45.677-15.729c-8.267-16.959-1.23-37.41,15.729-45.677s37.41-1.23,45.677,15.729
		C358.583,327.069,351.54,347.52,334.582,355.788L334.582,355.788z"
                />
                <path
                    fill="#C28B3B"
                    d="M326.728,358.505c-2.294,0.486-4.671,0.744-7.1,0.744l-0.909-0.01
		c-1.349-0.036-2.677-0.15-3.984-0.336c1.617,0.233,3.255,0.357,4.904,0.357C321.995,359.255,324.371,359.007,326.728,358.505
		L326.728,358.505z M348.404,343.469c-2.661,4.16-6.201,7.704-10.345,10.365C342.322,351.106,345.81,347.546,348.404,343.469
		L348.404,343.469z M290.811,343.459c2.754,4.33,6.366,7.859,10.5,10.474C297.089,351.251,293.498,347.665,290.811,343.459
		L290.811,343.459z M285.809,320.103c-0.599,4.046-0.481,8.226,0.434,12.334c-0.584-2.651-0.858-5.415-0.78-8.252
		C285.499,322.806,285.613,321.442,285.809,320.103L285.809,320.103z M352.977,317.737c0.579,2.651,0.853,5.41,0.78,8.242
		c-0.036,1.39-0.155,2.754-0.351,4.092C354.01,326.03,353.886,321.85,352.977,317.737L352.977,317.737z M301.192,296.309
		c-4.273,2.728-7.766,6.294-10.37,10.376C293.493,302.52,297.037,298.975,301.192,296.309L301.192,296.309z M337.842,296.19
		c4.237,2.677,7.839,6.263,10.536,10.469C345.613,302.334,341.986,298.804,337.842,296.19L337.842,296.19z M319.587,290.919
		c-2.403,0-4.826,0.253-7.224,0.78c2.336-0.506,4.754-0.77,7.234-0.77l0.915,0.01c1.405,0.036,2.79,0.16,4.149,0.362
		C322.981,291.043,321.287,290.919,319.587,290.919L319.587,290.919z"
                />
                <path
                    fill="#A6D7E6"
                    d="M312.358,291.694c2.398-0.522,4.821-0.78,7.224-0.78c1.705,0,3.395,0.129,5.069,0.377
		c4.8,0.713,9.27,2.418,13.192,4.898c4.144,2.609,7.771,6.139,10.536,10.469c2.144,3.343,3.725,7.084,4.599,11.078
		c0.909,4.113,1.028,8.293,0.429,12.339c-0.713,4.883-2.46,9.425-5.002,13.393c-2.594,4.077-6.082,7.637-10.345,10.365
		c-3.415,2.196-7.244,3.798-11.332,4.666c-2.356,0.506-4.733,0.749-7.094,0.749c-1.648,0-3.286-0.119-4.904-0.357
		c-4.893-0.698-9.44-2.434-13.419-4.96c-4.134-2.615-7.746-6.149-10.5-10.474c-2.129-3.328-3.695-7.048-4.568-11.022
		c-0.909-4.113-1.033-8.293-0.434-12.334c0.713-4.893,2.465-9.445,5.012-13.419c2.604-4.077,6.097-7.647,10.37-10.376
		C304.561,294.149,308.333,292.568,312.358,291.694L312.358,291.694z M338.286,278.663c8.087,3.271,15.129,8.593,20.477,15.321
		c-8.541-6.795-19.335-10.882-31.096-10.882c-27.634,0-50.033,22.399-50.033,50.033c0,11.76,4.087,22.555,10.882,31.096
		c-6.547-5.203-11.766-12.008-15.052-19.821c7.379,17.584,24.539,30.14,44.84,30.672l1.333,0.015
		c27.019,0,49.269-21.542,49.982-48.71C370.178,304.917,357.115,286.243,338.286,278.663L338.286,278.663z"
                />
                <path
                    fill="#FFFFFF"
                    d="M258.449,314.197c-1.395,0.677-3.023,0.878-4.635,0.45c-3.56-0.951-5.679-4.604-4.728-8.159
		c5.472-20.539,19.563-37.663,38.66-46.974c3.312-1.612,7.301-0.238,8.918,3.069c1.612,3.307,0.243,7.301-3.069,8.918
		c-15.615,7.616-27.143,21.619-31.618,38.423C261.451,311.862,260.129,313.376,258.449,314.197z"
                />
                <path
                    fill="#A6D7E6"
                    d="M319.607,275.051c-27.634,0-50.033,22.399-50.033,50.033c0,6.857,1.385,13.388,3.886,19.335
		c-2.661-6.34-4.051-13.331-3.86-20.648c0.713-27.174,22.963-48.71,49.982-48.71l1.338,0.016c6.139,0.16,11.988,1.421,17.367,3.586
		C332.52,276.333,326.216,275.051,319.607,275.051L319.607,275.051z"
                />
                <path
                    fill="#86C5DA"
                    d="M319.587,275.062c-27.019,0-49.269,21.542-49.982,48.711c-0.191,7.317,1.199,14.308,3.86,20.648
		c3.286,7.813,8.505,14.613,15.052,19.821c-6.795-8.541-10.882-19.335-10.882-31.096c0-27.634,22.4-50.033,50.033-50.033
		c11.76,0,22.555,4.087,31.096,10.882c-5.353-6.728-12.391-12.055-20.477-15.321c-5.379-2.165-11.228-3.426-17.367-3.586
		L319.587,275.062L319.587,275.062z"
                />
            </g>
        </svg>
    );
}

function IconMoon({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M233.54 142.23a8 8 0 0 0-8-2 88.08 88.08 0 0 1-109.8-109.8 8 8 0 0 0-10-10 104.84 104.84 0 0 0-52.91 37A104 104 0 0 0 136 224a103.09 103.09 0 0 0 62.52-20.88 104.84 104.84 0 0 0 37-52.91 8 8 0 0 0-1.98-7.98Zm-44.64 48.11A88 88 0 0 1 65.66 67.11a89 89 0 0 1 31.4-26A106 106 0 0 0 96 56a104.11 104.11 0 0 0 104 104 106 106 0 0 0 14.92-1.06 89 89 0 0 1-26.02 31.4Z" />
        </svg>
    );
}

function IconSun({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M120 40V16a8 8 0 0 1 16 0v24a8 8 0 0 1-16 0Zm72 88a64 64 0 1 1-64-64 64.07 64.07 0 0 1 64 64Zm-16 0a48 48 0 1 0-48 48 48.05 48.05 0 0 0 48-48ZM58.34 69.66a8 8 0 0 0 11.32-11.32l-16-16a8 8 0 0 0-11.32 11.32Zm0 116.68-16 16a8 8 0 0 0 11.32 11.32l16-16a8 8 0 0 0-11.32-11.32ZM192 72a8 8 0 0 0 5.66-2.34l16-16a8 8 0 0 0-11.32-11.32l-16 16A8 8 0 0 0 192 72Zm5.66 114.34a8 8 0 0 0-11.32 11.32l16 16a8 8 0 0 0 11.32-11.32ZM48 128a8 8 0 0 0-8-8H16a8 8 0 0 0 0 16h24a8 8 0 0 0 8-8Zm80 80a8 8 0 0 0-8 8v24a8 8 0 0 0 16 0v-24a8 8 0 0 0-8-8Zm112-88h-24a8 8 0 0 0 0 16h24a8 8 0 0 0 0-16Z" />
        </svg>
    );
}

function IconCopy({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M216 32H88a8 8 0 0 0-8 8v40H40a8 8 0 0 0-8 8v128a8 8 0 0 0 8 8h128a8 8 0 0 0 8-8v-40h40a8 8 0 0 0 8-8V40a8 8 0 0 0-8-8Zm-56 176H48V96h112Zm48-48h-32V88a8 8 0 0 0-8-8H96V48h112Z" />
        </svg>
    );
}

function IconCheck({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="m229.66 77.66-128 128a8 8 0 0 1-11.32 0l-56-56a8 8 0 0 1 11.32-11.32L96 188.69 218.34 66.34a8 8 0 0 1 11.32 11.32Z" />
        </svg>
    );
}

function IconDownload({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M224 152v56a16 16 0 0 1-16 16H48a16 16 0 0 1-16-16v-56a8 8 0 0 1 16 0v56h160v-56a8 8 0 0 1 16 0Zm-101.66 5.66a8 8 0 0 0 11.32 0l40-40a8 8 0 0 0-11.32-11.32L136 132.69V40a8 8 0 0 0-16 0v92.69l-26.34-26.35a8 8 0 0 0-11.32 11.32Z" />
        </svg>
    );
}

function IconClose({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path d="M205.66 194.34a8 8 0 0 1-11.32 11.32L128 139.31l-66.34 66.35a8 8 0 0 1-11.32-11.32L116.69 128 50.34 61.66a8 8 0 0 1 11.32-11.32L128 116.69l66.34-66.35a8 8 0 0 1 11.32 11.32L139.31 128Z" />
        </svg>
    );
}

function IconEdit({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
            />
        </svg>
    );
}

function IconShare({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            viewBox="0 0 256 256"
            {...props}
        >
            <path d="m237.66 106.35-80-80A8 8 0 0 0 144 32v40.35c-25.94 2.22-54.59 14.92-78.16 34.91-28.38 24.08-46.05 55.11-49.76 87.37a12 12 0 0 0 20.68 9.58c11-11.71 50.14-48.74 107.24-52V192a8 8 0 0 0 13.66 5.65l80-80a8 8 0 0 0 0-11.3ZM160 172.69V144a8 8 0 0 0-8-8c-28.08 0-55.43 7.33-81.29 21.8a196.17 196.17 0 0 0-36.57 26.52c5.8-23.84 20.42-46.51 42.05-64.86C99.41 99.77 127.75 88 152 88a8 8 0 0 0 8-8V51.32L220.69 112Z" />
        </svg>
    );
}

function IconUsers({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            viewBox="0 0 256 256"
            {...props}
        >
            <path d="M117.25 157.92a60 60 0 1 0-66.5 0 95.83 95.83 0 0 0-47.22 37.71 8 8 0 1 0 13.4 8.74 80 80 0 0 1 134.14 0 8 8 0 0 0 13.4-8.74 95.83 95.83 0 0 0-47.22-37.71ZM40 108a44 44 0 1 1 44 44 44.05 44.05 0 0 1-44-44Zm210.14 98.7a8 8 0 0 1-11.07-2.33A79.83 79.83 0 0 0 172 168a8 8 0 0 1 0-16 44 44 0 1 0-16.34-84.87 8 8 0 1 1-5.94-14.85 60 60 0 0 1 55.53 105.64 95.83 95.83 0 0 1 47.22 37.71 8 8 0 0 1-2.33 11.07Z" />
        </svg>
    );
}

function IconExternalLink({
    className,
    ...props
}: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            viewBox="0 0 256 256"
            {...props}
        >
            <path d="M224 104a8 8 0 0 1-16 0V59.32l-66.33 66.34a8 8 0 0 1-11.32-11.32L196.68 48H152a8 8 0 0 1 0-16h64a8 8 0 0 1 8 8Zm-40 24a8 8 0 0 0-8 8v72H48V80h72a8 8 0 0 0 0-16H48a16 16 0 0 0-16 16v128a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-72a8 8 0 0 0-8-8Z" />
        </svg>
    );
}

function IconChevronUpDown({
    className,
    ...props
}: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            viewBox="0 0 256 256"
            {...props}
        >
            <path d="M181.66 170.34a8 8 0 0 1 0 11.32l-48 48a8 8 0 0 1-11.32 0l-48-48a8 8 0 0 1 11.32-11.32L128 212.69l42.34-42.35a8 8 0 0 1 11.32 0Zm-96-84.68L128 43.31l42.34 42.35a8 8 0 0 0 11.32-11.32l-48-48a8 8 0 0 0-11.32 0l-48 48a8 8 0 0 0 11.32 11.32Z" />
        </svg>
    );
}

function IconChevronDownArrow({
    className,
    ...props
}: React.ComponentProps<'svg'>) {
    return (
        <svg
            width="800px"
            height="800px"
            viewBox="0 0 1024 1024"
            className={cn('h-4 w-4', className)}
            version="1.1"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            {...props}
        >
            <path
                d="M917.333333 364.8L851.2 298.666667 512 637.866667 172.8 298.666667 106.666667 364.8 512 768z"
                fill="#2196F3"
            />
        </svg>
    );
}

function IconHome({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            fill="currentColor"
            className={cn('h-4 w-4', className)}
            {...props}
        >
            <linearGradient
                id="jv689zNUBazMNK6AOyXtga"
                x1="6"
                x2="42"
                y1="41"
                y2="41"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0" stopColor="#c8d3de" />
                <stop offset="1" stopColor="#c8d3de" />
            </linearGradient>
            <path
                fill="url(#jv689zNUBazMNK6AOyXtga)"
                d="M42,39H6v2c0,1.105,0.895,2,2,2h32c1.105,0,2-0.895,2-2V39z"
            />
            <linearGradient
                id="jv689zNUBazMNK6AOyXtgb"
                x1="14.095"
                x2="31.385"
                y1="10.338"
                y2="43.787"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0" stopColor="#fcfcfc" />
                <stop offset=".495" stopColor="#f4f4f4" />
                <stop offset=".946" stopColor="#e8e8e8" />
                <stop offset="1" stopColor="#e8e8e8" />
            </linearGradient>
            <path
                fill="url(#jv689zNUBazMNK6AOyXtgb)"
                d="M42,39H6V20L24,3l18,17V39z"
            />
            <path
                fill="#de490d"
                d="M13,25h10c0.552,0,1,0.448,1,1v17H12V26C12,25.448,12.448,25,13,25z"
            />
            <path
                d="M24,4c-0.474,0-0.948,0.168-1.326,0.503l-5.359,4.811L6,20v5.39L24,9.428L42,25.39V20L30.685,9.314	l-5.359-4.811C24.948,4.168,24.474,4,24,4z"
                opacity=".05"
            />
            <path
                d="M24,3c-0.474,0-0.948,0.167-1.326,0.5l-5.359,4.784L6,18.909v5.359L24,8.397l18,15.871v-5.359	L30.685,8.284L25.326,3.5C24.948,3.167,24.474,3,24,3z"
                opacity=".07"
            />
            <linearGradient
                id="jv689zNUBazMNK6AOyXtgc"
                x1="24"
                x2="24"
                y1="1.684"
                y2="23.696"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0" stopColor="#d43a02" />
                <stop offset="1" stopColor="#b9360c" />
            </linearGradient>
            <path
                fill="url(#jv689zNUBazMNK6AOyXtgc)"
                d="M44.495,19.507L25.326,2.503C24.948,2.168,24.474,2,24,2s-0.948,0.168-1.326,0.503	L3.505,19.507c-0.42,0.374-0.449,1.02-0.064,1.43l1.636,1.745c0.369,0.394,0.984,0.424,1.39,0.067L24,7.428L41.533,22.75	c0.405,0.356,1.021,0.327,1.39-0.067l1.636-1.745C44.944,20.527,44.915,19.881,44.495,19.507z"
            />
            <linearGradient
                id="jv689zNUBazMNK6AOyXtgd"
                x1="28.05"
                x2="35.614"
                y1="25.05"
                y2="32.614"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0" stopColor="#33bef0" />
                <stop offset="1" stopColor="#0a85d9" />
            </linearGradient>
            <path
                fill="url(#jv689zNUBazMNK6AOyXtgd)"
                d="M29,25h6c0.552,0,1,0.448,1,1v6c0,0.552-0.448,1-1,1h-6c-0.552,0-1-0.448-1-1v-6	C28,25.448,28.448,25,29,25z"
            />
        </svg>
    );
}
function IconHelp({ className, ...props }: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 300 300"
            className={cn('h-5 w-5', className)}
            {...props}
        >
            <defs>
                <style>
                    {`.cls-1 {
                    fill: url(#linear-gradient);
                  }

                  .cls-2 {
                    fill: #fff;
                  }`}
                </style>
                <linearGradient
                    id="linear-gradient"
                    x1="150"
                    y1="3.03"
                    x2="150"
                    y2="296.97"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0" stopColor="#f1836c" />
                    <stop offset=".39" stopColor="#ee6e4e" />
                    <stop offset="1" stopColor="#ec5025" />
                </linearGradient>
            </defs>
            <ellipse
                className="cls-1"
                cx="150"
                cy="150"
                rx="144.65"
                ry="146.97"
            />
            <g>
                <path
                    className="cls-2"
                    d="m172.62,141.17c-4.29,4.58-4.98,5.3-6.11,6.77-2.05,2.68-3.83,4.97-4.87,8.18-.2.63-.84,2.71-.88,5.46-.04,2.32.39,2.6.48,4.74.06,1.42.2,5.1-1.94,8.64-1,1.65-2.4,3.1-4.27,4.34-1.02.67-2,.89-2.47,1.01-.76.2-3.81.97-6.95-.47-2.5-1.16-3.76-3.12-4.6-4.48-.62-.99-1.9-3.33-2.43-8.72-.58-6.02.26-10.96.93-13.85,1.26-5.43,3.25-9.33,4.07-10.84,2.73-5.05,5.9-8.41,8.75-11.46,3.91-4.17,5.68-6.2,9.22-10.26.49-.57,1.46-1.69,2.27-3.4.87-1.85,1.32-3.87,1.37-6.04-.14-9.41-6.13-15.82-15.02-16.05-8.52-.22-14.77,6.02-15.36,15.34-.1.92-.74,6.26-5.01,9.14-1.73,1.17-3.79,1.64-4.17,1.73-1.08.24-3.27.72-5.76-.06-3.5-1.1-5.25-3.96-5.79-4.84-1.62-2.65-1.75-5.19-1.85-7.32-.12-2.45.19-4.7.85-7.23,2.52-9.62,8.26-16.21,9.21-17.28,1.98-2.23,4.88-5.41,9.72-7.94,3.14-1.64,5.79-2.3,7.77-2.79,2.19-.54,7.09-1.54,13.33-1.02,3.08.25,8.99.8,15.47,4.29,1.47.79,11.53,6.42,16.37,18.41,2.22,5.51,2.56,10.43,2.71,12.87.16,2.7.36,6.34-.67,10.84-1.85,8.08-6.42,13.56-8.62,16.13-.08.09-1.97,2.11-5.75,6.15Z"
                />
                <ellipse
                    className="cls-2"
                    cx="150.47"
                    cy="213.31"
                    rx="14.66"
                    ry="14.36"
                />
            </g>
        </svg>
    );
}

export {
    IconArrowDown,
    IconArrowElbow,
    IconArrowRight,
    IconBitly,
    IconBilly,
    IconBitlyText,
    IconCheck,
    IconChevronUpDown,
    IconClose,
    IconCopy,
    IconDownload,
    IconEdit,
    IconExternalLink,
    IconChevronDownArrow,
    IconGitHub,
    IconGoogle,
    IconHome,
    IconHelp,
    IconMessage,
    IconMoon,
    IconNextChat,
    IconOpenAI,
    IconPlus,
    IconRefresh,
    IconSeparator,
    IconSettings,
    IconShare,
    IconSidebar,
    IconSpinner,
    IconStop,
    IconSun,
    IconTrash,
    IconUser,
    IconUsers,
    IconVercel
};
